package com.dotdrives.repository

import com.dotdrives.model.FormPaymentReadiness
import com.dotdrives.client.FormClient
import com.dotdrives.model.FormsDashboardSnapshot
import com.dotdrives.model.FormLogoUpload
import com.dotdrives.model.FormConfig
import com.dotdrives.model.FormSubmission
import com.dotdrives.model.GivingForm
import com.dotdrives.model.NewGivingForm
import com.dotdrives.model.Target

interface FormRepository {
    suspend fun uploadLogo(orgId: String, formId: String, name: String, mime: String, bytes: ByteArray): Result<FormLogoUpload>

    suspend fun dashboard(orgId: String): Result<FormsDashboardSnapshot>

    suspend fun targets(orgId: String): Result<List<Target>>

    suspend fun listForms(orgId: String): Result<List<GivingForm>>

    suspend fun getForm(orgId: String, formId: String): Result<GivingForm>

    suspend fun createForm(orgId: String, form: NewGivingForm): Result<GivingForm>

    /**
     * Persists `config` and `target_id` together and bumps `version` -- docs/schema.md:
     * "bumped on every save that changes config". `target_id` is a real column, not part of the
     * `config` JSONB document, but the builder's single "Save changes" action commits both at
     * once, so it travels alongside `config` here rather than through [saveSettings].
     */
    suspend fun saveConfig(orgId: String, formId: String, config: FormConfig, targetId: String?): Result<GivingForm>

    /** Persists the Settings-tab columns (name/headline/goal/progress bar/close date) -- no version bump. */
    suspend fun saveSettings(
        orgId: String,
        formId: String,
        name: String,
        headline: String,
        goalAmount: Double?,
        showProgressBar: Boolean,
        closeAt: kotlin.time.Instant?,
    ): Result<GivingForm>

    suspend fun setStatus(orgId: String, formId: String, status: String): Result<GivingForm>

    /**
     * Returns null when the financial query for this form sees no rows. The feed cannot
     * cross-validate against other
     * forms' visible rows (it is scoped to a single [formId]), so a form with genuinely zero
     * completed submissions in the window also reads as null -- the conservative choice, since
     * the alternative (claiming "no gifts yet" to a `field` role whose rows are actually hidden)
     * is the exact falsehood this contract exists to avoid.
     */
    suspend fun submissionFeed(orgId: String, formId: String, limit: Int): Result<List<FormSubmission>?>

    suspend fun paymentReadiness(orgId: String): Result<FormPaymentReadiness> =
        stripeConnected(orgId).map { if (it) FormPaymentReadiness.READY else FormPaymentReadiness.DISCONNECTED }

    suspend fun stripeConnected(orgId: String): Result<Boolean>
}

class SupabaseFormRepository(
    private val client: FormClient = FormClient(),
) : FormRepository {
    override suspend fun uploadLogo(orgId: String, formId: String, name: String, mime: String, bytes: ByteArray): Result<FormLogoUpload> =
        runCatching { client.uploadLogo(orgId, formId, name, mime, bytes) }

    override suspend fun dashboard(orgId: String): Result<FormsDashboardSnapshot> =
        runCatching { client.dashboard(orgId) }

    override suspend fun targets(orgId: String): Result<List<Target>> =
        runCatching { client.targets(orgId) }

    override suspend fun listForms(orgId: String): Result<List<GivingForm>> =
        runCatching { client.listForms(orgId) }

    override suspend fun getForm(orgId: String, formId: String): Result<GivingForm> =
        runCatching { client.getForm(orgId, formId) }

    override suspend fun createForm(orgId: String, form: NewGivingForm): Result<GivingForm> =
        runCatching { client.createForm(orgId, form) }

    override suspend fun saveConfig(
        orgId: String,
        formId: String,
        config: FormConfig,
        targetId: String?,
    ): Result<GivingForm> =
        runCatching { client.saveConfig(orgId, formId, config, targetId) }

    override suspend fun saveSettings(
        orgId: String,
        formId: String,
        name: String,
        headline: String,
        goalAmount: Double?,
        showProgressBar: Boolean,
        closeAt: kotlin.time.Instant?,
    ): Result<GivingForm> =
        runCatching { client.saveSettings(orgId, formId, name, headline, goalAmount, showProgressBar, closeAt) }

    override suspend fun setStatus(orgId: String, formId: String, status: String): Result<GivingForm> =
        runCatching { client.setStatus(orgId, formId, status) }

    override suspend fun submissionFeed(orgId: String, formId: String, limit: Int): Result<List<FormSubmission>?> =
        runCatching { client.submissionFeed(orgId, formId, limit) }

    override suspend fun paymentReadiness(orgId: String): Result<FormPaymentReadiness> =
        runCatching { client.paymentReadiness(orgId) }

    override suspend fun stripeConnected(orgId: String): Result<Boolean> =
        runCatching { client.stripeConnected(orgId) }
}
